import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Bell, Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { GlobalSearch } from "@/components/shared/GlobalSearch";
import { IdleSessionGuard } from "@/components/shared/IdleSessionGuard";
import { ShadowBanner } from "@/components/shared/ShadowBanner";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const UNREAD_POLL_MS = 30000;

export function WorkspaceShell({ workspaceLabel, navItems, orgName, workspaceType, children }) {
  const { user, logout, memberships } = useAuth();
  const clientOrg = memberships?.find((m) => m.org_type === "CLIENT");
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [unreadSupport, setUnreadSupport] = useState(0);

  // One real notification source per workspace - never a decorative bell.
  const BELL = {
    CLIENT: { path: "/client/support", count: () => api.get("/client/support/unread-count").then((r) => r.data.unread_count) },
    INTERNAL: { path: "/control-centre/support-inbox", count: () => api.get("/directory/clients/support-inbox").then((r) => r.data.items.reduce((n, i) => n + (i.unread_count || 0), 0)) },
    SUPPLIER: { path: "/supplier/compliance", count: () => api.get("/dashboard/supplier").then((r) => r.data.kpis?.pending_compliance || 0) },
  }[workspaceType];

  useEffect(() => {
    if (!BELL) return;
    const poll = () => BELL.count().then(setUnreadSupport).catch(() => {});
    poll();
    const interval = setInterval(poll, UNREAD_POLL_MS);
    window.addEventListener("jigsaw:support-read", poll);
    return () => { clearInterval(interval); window.removeEventListener("jigsaw:support-read", poll); };
  }, [workspaceType, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (path) => (path === navItems[0].path ? location.pathname === path : location.pathname.startsWith(path));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user?.full_name?.split(" ").map((n) => n[0]).join("").slice(0, 2);

  return (
    <div className="flex min-h-screen bg-jigsaw-bg">
      <IdleSessionGuard />
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-[242px] shrink-0 flex-col bg-[#10202B] text-white transition-transform duration-200 md:relative md:z-auto md:translate-x-0",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-[92px] flex-col justify-center border-b border-white/10 px-4">
          <div className="flex items-center gap-3">
            <img src="/brand/jigsaw-icon.png" alt="Jigsaw" className="h-[52px] w-[52px] shrink-0 object-contain" />
            <p className="text-[30px] font-bold leading-none tracking-tight">Jigsaw</p>
          </div>
          <p className="mt-1.5 whitespace-nowrap text-[11px] leading-tight tracking-wide text-white/65">Accommodation · Support · People</p>
          <button data-testid="mobile-nav-close" onClick={() => setMobileNavOpen(false)} className="text-white/60 hover:text-white md:hidden">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileNavOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={cn(
                  "group relative flex h-[46px] items-center gap-3 rounded-md pl-4 pr-3 text-[15px] font-medium transition-colors duration-150",
                  active ? "bg-white/[0.08] text-white" : "text-white/60 hover:bg-white/[0.06] hover:text-white/90"
                )}
              >
                <span
                  className={cn(
                    "absolute left-0 top-1/2 h-4 -translate-y-1/2 rounded-r-full bg-jigsaw-orange transition-all duration-150",
                    active ? "w-[3px] opacity-100" : "w-0 opacity-0"
                  )}
                />
                <Icon strokeWidth={1.75} className={cn("h-5 w-5 shrink-0 transition-colors", active ? "text-jigsaw-orange" : "text-white/45 group-hover:text-white/70")} />
                <span className="truncate">{item.label}</span>
                {!item.implemented && (
                  <span className="ml-auto rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-white/50">Soon</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 px-4 pb-4 pt-2">
          <div data-testid="rail-help-card" className="rounded-lg bg-white/[0.07] p-4">
            <p className="text-sm font-semibold text-white">Need help?</p>
            <p className="mt-1 text-xs leading-relaxed text-white/60">{workspaceType === "CLIENT" ? "Contact the Jigsaw team about any request or booking." : workspaceType === "SUPPLIER" ? "Questions about a request? Message the Jigsaw team." : "Client support requests and replies live in the Support Inbox."}</p>
            <Link data-testid="rail-help-link" to={workspaceType === "CLIENT" ? "/client/support" : workspaceType === "SUPPLIER" ? "/supplier/requests" : "/control-centre/support-inbox"}
              onClick={() => setMobileNavOpen(false)} className="mt-3 inline-flex h-8 items-center rounded-md bg-jigsaw-orange px-3 text-xs font-semibold text-white hover:bg-jigsaw-orangeDark">
              {workspaceType === "INTERNAL" ? "Open inbox" : "Contact support"}
            </Link>
          </div>
          {workspaceType === "CLIENT" && clientOrg && (
            <div data-testid="rail-client-org" className="px-2 pt-4">
              <p className="truncate text-[15px] font-semibold text-white">{clientOrg.organisation_name}</p>
              {clientOrg.client_since && <p className="text-xs text-white/60">Client since {clientOrg.client_since}</p>}
            </div>
          )}
        </div>

      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="flex h-[62px] items-center gap-3 border-b border-jigsaw-border bg-white px-4 md:gap-4 md:px-6">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setMobileNavOpen(true)}
            className="rounded-md p-2 text-jigsaw-muted hover:bg-jigsaw-bg md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <button
              data-testid="notification-bell"
              onClick={() => BELL && navigate(BELL.path)}
              aria-label={workspaceType === "SUPPLIER" ? "Compliance items needing attention" : "Unread support messages"}
              className="relative rounded-md p-2 text-jigsaw-muted hover:bg-jigsaw-bg"
            >
              <Bell className="h-5 w-5" />
              {unreadSupport > 0 && (
                <span data-testid="notification-bell-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-jigsaw-danger px-1 text-[10px] font-semibold text-white">
                  {unreadSupport}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="identity-menu-trigger" className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-jigsaw-bg">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-jigsaw-orange text-xs font-semibold text-white">
                    {initials}
                  </div>
                  <div className="hidden min-w-0 text-left sm:block">
                    <p className="truncate text-sm font-medium text-jigsaw-navy leading-tight">{user?.full_name}</p>
                    <p className="truncate text-[11px] text-jigsaw-muted leading-tight">{orgName}</p>
                  </div>
                  <ChevronDown className="hidden h-3.5 w-3.5 text-jigsaw-muted sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium text-jigsaw-navy">{user?.full_name}</p>
                  <p className="text-xs font-normal text-jigsaw-muted">{orgName}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout} className="cursor-pointer text-jigsaw-danger focus:text-jigsaw-danger">
                  <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 md:p-6"><ShadowBanner />{children}</main>
      </div>
    </div>
  );
}
